export type Listener<T> = (payload: T) => void;
export type Unsubscribe = () => void;

/**
 * Minimal typed pub/sub used to decouple simulation systems from the UI.
 *
 * Simulation code emits; React subscribes. Nothing in `src/game` or below is
 * allowed to import from `src/ui`, and this bus is how that stays true.
 */
export class EventBus<Events extends Record<string, unknown>> {
  private listeners = new Map<keyof Events, Set<Listener<never>>>();

  on<K extends keyof Events>(event: K, listener: Listener<Events[K]>): Unsubscribe {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(listener as Listener<never>);
    return () => {
      set!.delete(listener as Listener<never>);
    };
  }

  once<K extends keyof Events>(event: K, listener: Listener<Events[K]>): Unsubscribe {
    const off = this.on(event, (payload) => {
      off();
      listener(payload);
    });
    return off;
  }

  emit<K extends keyof Events>(event: K, payload: Events[K]): void {
    const set = this.listeners.get(event);
    if (!set) return;
    // Copy so a listener can safely unsubscribe during dispatch.
    for (const l of Array.from(set)) {
      try {
        (l as Listener<Events[K]>)(payload);
      } catch (err) {
        console.error(`[EventBus] listener for "${String(event)}" threw`, err);
      }
    }
  }

  clear(): void {
    this.listeners.clear();
  }

  listenerCount<K extends keyof Events>(event: K): number {
    return this.listeners.get(event)?.size ?? 0;
  }
}
